// frontend/src/components/CampaignManager/dialogs/ActionDialog.tsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
  FormControlLabel,
  Checkbox,
  Box,
  Divider,
  Typography,
  IconButton,
  InputAdornment,
  Autocomplete,
  Chip,
  Alert
} from '@mui/material';
import { MagicWand, Plus } from '@phosphor-icons/react';
import { Action, Campaign, Lottery, Werbeweg, Werbetraeger, Vermittler, PhoneNumber } from '../types';
import axios from 'axios';
import config from '../../../config';

// Format WKZ with leading zeros to 5 digits
const formatWKZ = (wkz: string | number | undefined | null): string => {
  if (wkz === undefined || wkz === null || wkz === '') return '';
  const str = String(wkz).replace(/^0+/, ''); // Remove existing leading zeros first
  return str.padStart(5, '0');
};

interface Props {
  open: boolean;
  action: Action | null;
  campaigns: Campaign[];
  lotteries: Lottery[];
  werbewege: Werbeweg[];
  werbetraeger: Werbetraeger[];
  vermittler: Vermittler[];
  phoneNumbers: PhoneNumber[];
  onClose: () => void;
  onSave: (action: Partial<Action>, id?: number) => Promise<void>;
  onRefreshWerbewege?: () => Promise<void>;
  onRefreshWerbetraeger?: () => Promise<void>;
  onRefreshVermittler?: () => Promise<void>;
}

export default function ActionDialog({
  open,
  action,
  campaigns,
  lotteries,
  werbewege,
  werbetraeger,
  vermittler,
  phoneNumbers,
  onClose,
  onSave,
  onRefreshWerbewege,
  onRefreshWerbetraeger,
  onRefreshVermittler
}: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [form, setForm] = useState({
    campaign_id: 0,
    lotterie_id: 0,
    werbeweg_id: 0,
    werbetraeger_id: 0,
    type: '',
    wkz: '',
    key_aktion: '',
    beschreibung: '',
    aktiv: true,
    start_datum: '',
    kostenart: 'Fix',
    menge_auflage: 0,
    anzahl_leads: 0,
    preis_pro_lead: '',
    fixpreis: '',
    tkp_preis: '',
    vermittler_id: 0,
    produktionskosten_einheit: '',
    produktionskosten_fix: '',
    telefon_nr_id: 0,
    versandmerker: '',
    bemerkungen_aktion: ''
  });

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [generatingWKZ, setGeneratingWKZ] = useState(false);
  const [callcenterWkzs, setCallcenterWkzs] = useState<Array<{
    id: number;
    callcenter_id: number;
    callcenter_name: string;
    wkz: string;
    los_key: string;
    aktiv: boolean;
  }>>([]);
  const [loadingWkzs, setLoadingWkzs] = useState(false);

  // Load Callcenter-WKZs when editing an action
  useEffect(() => {
    if (action?.id && open) {
      setLoadingWkzs(true);
      const token = localStorage.getItem('auth_token');
      axios.get(`${config.API_V1}/actions/${action.id}/callcenter-wkz`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      })
        .then(res => setCallcenterWkzs(res.data))
        .catch(err => console.error('Error loading callcenter WKZs:', err))
        .finally(() => setLoadingWkzs(false));
    } else {
      setCallcenterWkzs([]);
    }
  }, [action?.id, open]);

  // Local copies of lists that can be updated when creating new entries
  const [localWerbewege, setLocalWerbewege] = useState<Werbeweg[]>([]);
  const [localWerbetraeger, setLocalWerbetraeger] = useState<Werbetraeger[]>([]);
  const [localVermittler, setLocalVermittler] = useState<Vermittler[]>([]);

  // Initialize local lists when props change
  useEffect(() => {
    setLocalWerbewege(werbewege);
  }, [werbewege]);

  useEffect(() => {
    setLocalWerbetraeger(werbetraeger);
  }, [werbetraeger]);

  useEffect(() => {
    setLocalVermittler(vermittler);
  }, [vermittler]);

  // Helper-Funktion für deutsche Zahlenformatierung
  const parseGermanNumber = (value: string | number | null | undefined): number => {
    if (value === null || value === undefined || value === '') return 0;
    // If already a number, return it
    if (typeof value === 'number') return value;
    // If string, parse it - could be German format (1.234,56) or US format (1234.56)
    const str = String(value);
    // Check if it's German format (has comma as decimal separator)
    if (str.includes(',')) {
      // German format: remove thousand separators (.) and replace comma with dot
      const normalized = str.replace(/\./g, '').replace(',', '.');
      return parseFloat(normalized) || 0;
    }
    // US/API format: just parse directly
    return parseFloat(str) || 0;
  };

  const formatGermanNumber = (value: number | string | null | undefined): string => {
    if (value === null || value === undefined || value === '') return '';
    const num = typeof value === 'number' ? value : parseFloat(String(value)) || 0;
    if (num === 0) return '';
    return num.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Berechnungen
  // Stückkosten = Menge × Stückpreis (preis_pro_lead ist der Stückpreis aus Access)
  const stueckkosten = (form.menge_auflage || 0) * parseGermanNumber(form.preis_pro_lead);
  // Fixe Produktionskosten (zusätzlich)
  const produktionskostenFix = parseGermanNumber(form.produktionskosten_fix);
  // Produktionskosten gesamt (Einheitspreis ist in preis_pro_lead)
  const produktionskostenGesamt = stueckkosten + produktionskostenFix;

  // Hauptkosten = Fixpreis
  const fixpreis = parseGermanNumber(form.fixpreis);

  // Gesamtkosten = Fixpreis + Produktionskosten (Menge × Stückpreis + fixe Produktionskosten)
  const gesamtkosten = fixpreis + produktionskostenGesamt;

  useEffect(() => {
    if (action) {
      setForm({
        campaign_id: action.campaign_id,
        lotterie_id: action.lotterie_id || 0,
        werbeweg_id: action.werbeweg_id || 0,
        werbetraeger_id: action.werbetraeger_id || 0,
        type: action.type,
        wkz: action.wkz || '',
        key_aktion: formatWKZ(action.key_aktion),
        beschreibung: action.beschreibung || '',
        aktiv: action.aktiv !== false,
        start_datum: action.start_datum ? action.start_datum.split('T')[0] : '',
        kostenart: action.kostenart || 'Fix',
        menge_auflage: action.menge_auflage || 0,
        anzahl_leads: action.anzahl_leads || 0,
        preis_pro_lead: action.preis_pro_lead ? formatGermanNumber(action.preis_pro_lead) : '',
        fixpreis: action.fixpreis ? formatGermanNumber(action.fixpreis) : '',
        tkp_preis: (action as any).tkp_preis ? formatGermanNumber((action as any).tkp_preis) : '',
        vermittler_id: action.vermittler_id || 0,
        produktionskosten_einheit: action.produktionskosten_einheit ? formatGermanNumber(action.produktionskosten_einheit) : '',
        produktionskosten_fix: action.produktionskosten_fix ? formatGermanNumber(action.produktionskosten_fix) : '',
        telefon_nr_id: action.telefon_nr_id || 0,
        versandmerker: action.versandmerker || '',
        bemerkungen_aktion: action.bemerkungen_aktion || ''
      });
    } else {
      setForm({
        campaign_id: campaigns.length > 0 ? campaigns[0].id : 0,
        lotterie_id: lotteries.length > 0 ? lotteries[0].id : 0,
        werbeweg_id: werbewege.length > 0 ? werbewege[0].id : 0,
        werbetraeger_id: werbetraeger.length > 0 ? werbetraeger[0].id : 0,
        type: '',
        wkz: '',
        key_aktion: '',
        beschreibung: '',
        aktiv: true,
        start_datum: new Date().toISOString().split('T')[0],
        kostenart: 'Fix',
        menge_auflage: 0,
        anzahl_leads: 0,
        preis_pro_lead: '',
        fixpreis: '',
        tkp_preis: '',
        vermittler_id: vermittler.length > 0 ? vermittler[0].id : 0,
        produktionskosten_einheit: '',
        produktionskosten_fix: '',
        telefon_nr_id: phoneNumbers.length > 0 ? phoneNumbers[0].id_tel_nr : 0,
        versandmerker: '',
        bemerkungen_aktion: ''
      });
    }
  }, [action, campaigns, lotteries, werbewege, werbetraeger, vermittler, phoneNumbers, open]);

  const handleGenerateWKZ = async () => {
    setGeneratingWKZ(true);
    try {
      const response = await axios.post(`${config.API_V1}/actions/generate-wkz`, {
        lotterie_id: form.lotterie_id,
        campaign_id: form.campaign_id,
        werbeweg_id: form.werbeweg_id,
        vermittler_id: form.vermittler_id,
        werbetraeger_id: form.werbetraeger_id
      });
      setForm({ ...form, wkz: response.data.wkz });
    } catch (error) {
      console.error('Error generating WKZ:', error);
      alert('Fehler beim Generieren der WKZ');
    } finally {
      setGeneratingWKZ(false);
    }
  };

  // Create new Werbeweg
  const handleCreateWerbeweg = async (bezeichnung: string) => {
    try {
      const kurz = bezeichnung.substring(0, 3).toUpperCase();
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(`${config.API_V1}/werbewege`, {
        bezeichnung,
        kurz,
        bemerkungen: '',
        active: true
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const newWerbeweg = response.data;
      // Refresh parent FIRST, then set local + form with functional updaters
      if (onRefreshWerbewege) {
        await onRefreshWerbewege();
      }
      setLocalWerbewege(prev => prev.some(w => w.id === newWerbeweg.id) ? prev : [...prev, newWerbeweg]);
      setForm(prev => ({ ...prev, werbeweg_id: newWerbeweg.id }));
      return newWerbeweg;
    } catch (error) {
      console.error('Error creating werbeweg:', error);
      alert('Fehler beim Erstellen des Werbewegs');
      return null;
    }
  };

  // Create new Werbeträger
  const handleCreateWerbetraeger = async (bezeichnung: string) => {
    try {
      const kurz = bezeichnung.substring(0, 3).toUpperCase();
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(`${config.API_V1}/werbetraeger`, {
        bezeichnung,
        kurz,
        bemerkungen: '',
        active: true
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const newWerbetraeger = response.data;
      // Refresh parent FIRST, then set local + form with functional updaters
      if (onRefreshWerbetraeger) {
        await onRefreshWerbetraeger();
      }
      setLocalWerbetraeger(prev => prev.some(wt => wt.id === newWerbetraeger.id) ? prev : [...prev, newWerbetraeger]);
      setForm(prev => ({ ...prev, werbetraeger_id: newWerbetraeger.id }));
      return newWerbetraeger;
    } catch (error) {
      console.error('Error creating werbetraeger:', error);
      alert('Fehler beim Erstellen des Werbeträgers');
      return null;
    }
  };

  // Create new Vermittler
  const handleCreateVermittler = async (bezeichnung: string) => {
    try {
      const kurz = bezeichnung.substring(0, 3).toUpperCase();
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(`${config.API_V1}/vermittler`, {
        bezeichnung,
        kurz,
        bemerkungen: '',
        active: true
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const newVermittler = response.data;
      // Refresh parent FIRST, then set local + form with functional updaters
      if (onRefreshVermittler) {
        await onRefreshVermittler();
      }
      setLocalVermittler(prev => prev.some(v => v.id === newVermittler.id) ? prev : [...prev, newVermittler]);
      setForm(prev => ({ ...prev, vermittler_id: newVermittler.id }));
      return newVermittler;
    } catch (error: any) {
      console.error('Error creating vermittler:', error);
      const message = error.response?.data?.error || 'Fehler beim Erstellen des Vermittlers';
      alert(message);
      return null;
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      // Konvertiere deutsche Zahlenformate zurück zu Zahlen
      const dataToSave = {
        ...form,
        preis_pro_lead: parseGermanNumber(form.preis_pro_lead),
        fixpreis: parseGermanNumber(form.fixpreis),
        tkp_preis: parseGermanNumber(form.tkp_preis),
        produktionskosten_einheit: parseGermanNumber(form.produktionskosten_einheit),
        produktionskosten_fix: parseGermanNumber(form.produktionskosten_fix)
      };
      await onSave(dataToSave, action?.id);
      onClose();
    } catch (error: any) {
      console.error('Error saving action:', error);
      const msg = error?.response?.data?.error || error?.message || 'Fehler beim Speichern';
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose()}
      maxWidth="xl"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        {action ? 'Aktion bearbeiten' : 'Neue Aktion'}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ pt: 2 }}>
          {saveError && (
            <Alert severity="error" onClose={() => setSaveError(null)}>{saveError}</Alert>
          )}
          {/* Hauptauswahl */}
          <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 2 }}>
            <FormControl fullWidth required>
              <InputLabel>Kampagne</InputLabel>
              <Select
                value={form.campaign_id}
                onChange={(e) => setForm({ ...form, campaign_id: Number(e.target.value) })}
                label="Kampagne"
                disabled={saving}
              >
                {campaigns.map(c => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth required>
              <InputLabel>Lotterie</InputLabel>
              <Select
                value={form.lotterie_id}
                onChange={(e) => setForm({ ...form, lotterie_id: Number(e.target.value) })}
                label="Lotterie"
                disabled={saving}
              >
                {lotteries.map(l => (
                  <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="WKZ (numerisch)"
              value={form.key_aktion}
              onChange={(e) => setForm({ ...form, key_aktion: e.target.value })}
              onBlur={(e) => setForm({ ...form, key_aktion: formatWKZ(e.target.value) })}
              disabled={saving}
              helperText="5-stellig mit führenden Nullen, z.B. 07569"
              inputProps={{ style: { fontFamily: 'monospace' } }}
            />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 2 }}>
            <TextField
              fullWidth
              type="date"
              label="Start"
              value={form.start_datum}
              onChange={(e) => setForm({ ...form, start_datum: e.target.value })}
              disabled={saving}
              InputLabelProps={{ shrink: true }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={form.aktiv}
                  onChange={(e) => setForm({ ...form, aktiv: e.target.checked })}
                  disabled={saving}
                />
              }
              label="Aktion ist aktiv"
            />
          </Box>

          <Divider />

          {/* Werbekanäle */}
          <Typography variant="h6">Werbekanäle</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 2 }}>
            <Autocomplete
              key={`werbeweg-${form.werbeweg_id}`}
              options={localWerbewege}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                if ((option as any).inputValue) return (option as any).inputValue;
                return option.bezeichnung;
              }}
              value={localWerbewege.find(w => w.id === form.werbeweg_id) || null}
              onChange={async (_, newValue) => {
                console.log('Werbeweg onChange:', newValue);
                if (typeof newValue === 'string') {
                  // User typed and pressed enter - create new
                  console.log('Creating werbeweg from string:', newValue);
                  await handleCreateWerbeweg(newValue);
                } else if (newValue && (newValue as any).inputValue) {
                  // User clicked "Add ..." option
                  console.log('Creating werbeweg from inputValue:', (newValue as any).inputValue);
                  await handleCreateWerbeweg((newValue as any).inputValue);
                } else if (newValue) {
                  // User selected existing option
                  console.log('Selected existing werbeweg:', newValue);
                  setForm({ ...form, werbeweg_id: newValue.id });
                } else {
                  // Cleared
                  setForm({ ...form, werbeweg_id: 0 });
                }
              }}
              filterOptions={(options, params) => {
                const filtered = options.filter(option =>
                  option.bezeichnung.toLowerCase().includes(params.inputValue.toLowerCase())
                );
                const { inputValue } = params;
                const isExisting = options.some((option) => inputValue.toLowerCase() === option.bezeichnung.toLowerCase());
                if (inputValue !== '' && !isExisting) {
                  filtered.push({
                    inputValue,
                    bezeichnung: `+ "${inputValue}" neu erstellen`,
                    id: -1,
                    kurz: '',
                    active: true
                  } as any);
                }
                return filtered;
              }}
              freeSolo
              selectOnFocus
              handleHomeEndKeys
              disabled={saving}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {(option as any).inputValue ? (
                    <span style={{ color: '#1976d2', fontWeight: 500 }}>
                      + "{(option as any).inputValue}" neu erstellen
                    </span>
                  ) : (
                    option.bezeichnung
                  )}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Werbeweg"
                  placeholder="Tippen zum Suchen oder Erstellen..."
                />
              )}
            />

            <Autocomplete
              key={`vermittler-${form.vermittler_id}`}
              options={localVermittler}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                if ((option as any).inputValue) return (option as any).inputValue;
                return option.bezeichnung;
              }}
              value={localVermittler.find(v => v.id === form.vermittler_id) || null}
              onChange={async (_, newValue) => {
                console.log('Vermittler onChange:', newValue);
                if (typeof newValue === 'string') {
                  await handleCreateVermittler(newValue);
                } else if (newValue && (newValue as any).inputValue) {
                  await handleCreateVermittler((newValue as any).inputValue);
                } else if (newValue) {
                  setForm({ ...form, vermittler_id: newValue.id });
                } else {
                  setForm({ ...form, vermittler_id: 0 });
                }
              }}
              filterOptions={(options, params) => {
                const filtered = options.filter(option =>
                  option.bezeichnung.toLowerCase().includes(params.inputValue.toLowerCase())
                );
                const { inputValue } = params;
                const isExisting = options.some((option) => inputValue.toLowerCase() === option.bezeichnung.toLowerCase());
                if (inputValue !== '' && !isExisting) {
                  filtered.push({
                    inputValue,
                    bezeichnung: `+ "${inputValue}" neu erstellen`,
                    id: -1,
                    kurz: '',
                    active: true
                  } as any);
                }
                return filtered;
              }}
              freeSolo
              selectOnFocus
              handleHomeEndKeys
              disabled={saving}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {(option as any).inputValue ? (
                    <span style={{ color: '#1976d2', fontWeight: 500 }}>
                      + "{(option as any).inputValue}" neu erstellen
                    </span>
                  ) : (
                    option.bezeichnung
                  )}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Vermittler/Lieferanten"
                  placeholder="Tippen zum Suchen oder Erstellen..."
                />
              )}
            />

            <Autocomplete
              key={`werbetraeger-${form.werbetraeger_id}`}
              options={localWerbetraeger}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                if ((option as any).inputValue) return (option as any).inputValue;
                return option.bezeichnung;
              }}
              value={localWerbetraeger.find(wt => wt.id === form.werbetraeger_id) || null}
              onChange={async (_, newValue) => {
                console.log('Werbetraeger onChange:', newValue);
                if (typeof newValue === 'string') {
                  await handleCreateWerbetraeger(newValue);
                } else if (newValue && (newValue as any).inputValue) {
                  await handleCreateWerbetraeger((newValue as any).inputValue);
                } else if (newValue) {
                  setForm({ ...form, werbetraeger_id: newValue.id });
                } else {
                  setForm({ ...form, werbetraeger_id: 0 });
                }
              }}
              filterOptions={(options, params) => {
                const filtered = options.filter(option =>
                  option.bezeichnung.toLowerCase().includes(params.inputValue.toLowerCase())
                );
                const { inputValue } = params;
                const isExisting = options.some((option) => inputValue.toLowerCase() === option.bezeichnung.toLowerCase());
                if (inputValue !== '' && !isExisting) {
                  filtered.push({
                    inputValue,
                    bezeichnung: `+ "${inputValue}" neu erstellen`,
                    id: -1,
                    kurz: '',
                    active: true
                  } as any);
                }
                return filtered;
              }}
              freeSolo
              selectOnFocus
              handleHomeEndKeys
              disabled={saving}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {(option as any).inputValue ? (
                    <span style={{ color: '#1976d2', fontWeight: 500 }}>
                      + "{(option as any).inputValue}" neu erstellen
                    </span>
                  ) : (
                    option.bezeichnung
                  )}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Werbeträger/Listen"
                  placeholder="Tippen zum Suchen oder Erstellen..."
                />
              )}
            />
          </Box>

          <Divider />

          {/* WKZ-Text */}
          <Typography variant="h6">WKZ-Text (Werbekennzeichen)</Typography>
          <TextField
            fullWidth
            label="WKZ-Text"
            value={form.wkz}
            onChange={(e) => setForm({ ...form, wkz: e.target.value })}
            disabled={saving}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleGenerateWKZ}
                    disabled={saving || generatingWKZ || !form.lotterie_id || !form.campaign_id}
                    edge="end"
                  >
                    <MagicWand size={20} />
                  </IconButton>
                </InputAdornment>
              )
            }}
            helperText="Textuelle Beschreibung des WKZ. Klicken Sie auf das Zauberstab-Icon zum Generieren."
          />

          {/* Callcenter-WKZs - nur bei bestehenden Aktionen anzeigen */}
          {action?.id && (
            <>
              <Divider />
              <Typography variant="h6">Callcenter-WKZs ({callcenterWkzs.length})</Typography>
              {loadingWkzs ? (
                <Typography variant="body2" color="text.secondary">Laden...</Typography>
              ) : callcenterWkzs.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Keine Callcenter-WKZs vorhanden</Typography>
              ) : (
                <Box sx={{
                  maxHeight: 200,
                  overflow: 'auto',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1
                }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f5f5f5' }}>
                        <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Callcenter</th>
                        <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>WKZ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {callcenterWkzs.map((cwkz) => (
                        <tr key={cwkz.id} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '6px 8px' }}>{cwkz.callcenter_name}</td>
                          <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{formatWKZ(cwkz.los_key)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              )}
            </>
          )}

          <Divider />

          {/* Kosten */}
          <Typography variant="h6">Kosten</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Kostenart</InputLabel>
              <Select
                value={form.kostenart}
                onChange={(e) => setForm({ ...form, kostenart: e.target.value })}
                label="Kostenart"
                disabled={saving}
              >
                <MenuItem value="Fix">Fix</MenuItem>
                <MenuItem value="TKP">TKP (Tausend-Kontakt-Preis)</MenuItem>
                <MenuItem value="Per Lead">Per Lead</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              type="number"
              label="Menge/Auflage"
              value={form.menge_auflage}
              onChange={(e) => setForm({ ...form, menge_auflage: Number(e.target.value) })}
              disabled={saving}
              inputProps={{ min: 0 }}
            />

            <TextField
              fullWidth
              type="number"
              label="Anzahl Leads"
              value={form.anzahl_leads}
              onChange={(e) => setForm({ ...form, anzahl_leads: Number(e.target.value) })}
              disabled={saving}
              inputProps={{ min: 0 }}
              helperText="Aus BCV Tool (optional)"
            />
          </Box>

          {/* Kostenart-spezifische Felder */}
          {form.kostenart === 'Fix' && (
            <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 2 }}>
              <TextField
                fullWidth
                label="Fixpreis (€)"
                value={form.fixpreis}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^\d,]/g, '');
                  setForm({ ...form, fixpreis: value });
                }}
                disabled={saving}
                placeholder="0,00"
                helperText="Fester Preis der Aktion"
              />
              <TextField
                fullWidth
                label="Stückpreis (€)"
                value={form.preis_pro_lead}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^\d,]/g, '');
                  setForm({ ...form, preis_pro_lead: value });
                }}
                disabled={saving}
                placeholder="0,00"
                helperText="Preis pro Einheit (z.B. 0,06)"
              />
            </Box>
          )}

          {form.kostenart === 'TKP' && (
            <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr', gap: 2 }}>
              <TextField
                fullWidth
                label="TKP-Preis (€)"
                value={form.tkp_preis}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^\d,]/g, '');
                  setForm({ ...form, tkp_preis: value });
                }}
                disabled={saving}
                placeholder="0,00"
                helperText="Preis pro 1.000 Kontakte"
              />
            </Box>
          )}

          {form.kostenart === 'Per Lead' && (
            <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr', gap: 2 }}>
              <TextField
                fullWidth
                label="Preis pro Lead (€)"
                value={form.preis_pro_lead}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^\d,]/g, '');
                  setForm({ ...form, preis_pro_lead: value });
                }}
                disabled={saving}
                placeholder="0,00"
                helperText="Preis pro generiertem Lead"
              />
            </Box>
          )}

          <Divider />

          {/* Zusätzliche Produktionskosten */}
          <Typography variant="h6">Zusätzliche Kosten</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 2 }}>
            <TextField
              fullWidth
              label="Fixe Produktionskosten (€)"
              value={form.produktionskosten_fix}
              onChange={(e) => {
                const value = e.target.value.replace(/[^\d,]/g, '');
                setForm({ ...form, produktionskosten_fix: value });
              }}
              disabled={saving}
              placeholder="0,00"
              helperText="Zusätzliche fixe Kosten"
            />

            <TextField
              fullWidth
              label="Produktionskosten/Einheit (€)"
              value={form.produktionskosten_einheit}
              onChange={(e) => {
                const value = e.target.value.replace(/[^\d,]/g, '');
                setForm({ ...form, produktionskosten_einheit: value });
              }}
              disabled={saving}
              placeholder="0,00"
              helperText="Kosten pro Einheit Produktion"
            />
          </Box>

          {/* Berechnungen */}
          <Box sx={{
            p: 2,
            bgcolor: 'action.hover',
            borderRadius: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 1
          }}>
            {form.kostenart === 'Fix' && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Fixpreis:</Typography>
                  <Typography variant="body1" color="primary">
                    {fixpreis.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Stückkosten ({(form.menge_auflage || 0).toLocaleString('de-DE')} x {parseGermanNumber(form.preis_pro_lead).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}):
                  </Typography>
                  <Typography variant="body1" color="primary">
                    {stueckkosten.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                  </Typography>
                </Box>
              </>
            )}
            {form.kostenart === 'TKP' && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  TKP ({parseGermanNumber(form.tkp_preis).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })} x {(form.menge_auflage || 0).toLocaleString('de-DE')} / 1.000):
                </Typography>
                <Typography variant="body1" color="primary">
                  {(parseGermanNumber(form.tkp_preis) * (form.menge_auflage || 0) / 1000).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                </Typography>
              </Box>
            )}
            {form.kostenart === 'Per Lead' && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Per Lead ({parseGermanNumber(form.preis_pro_lead).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })} x {(form.anzahl_leads || 0).toLocaleString('de-DE')}):
                </Typography>
                <Typography variant="body1" color="primary">
                  {(parseGermanNumber(form.preis_pro_lead) * (form.anzahl_leads || 0)).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                </Typography>
              </Box>
            )}
            {produktionskostenFix > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Fixe Produktionskosten:</Typography>
                <Typography variant="body1" color="primary">
                  {produktionskostenFix.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                </Typography>
              </Box>
            )}
            <Divider />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body1" fontWeight="bold">Gesamtkosten:</Typography>
              <Typography variant="h5" color="primary" fontWeight="bold">
                {(() => {
                  let hauptkosten = 0;
                  if (form.kostenart === 'Fix') hauptkosten = fixpreis + stueckkosten;
                  else if (form.kostenart === 'TKP') hauptkosten = parseGermanNumber(form.tkp_preis) * (form.menge_auflage || 0) / 1000;
                  else if (form.kostenart === 'Per Lead') hauptkosten = parseGermanNumber(form.preis_pro_lead) * (form.anzahl_leads || 0);
                  return (hauptkosten + produktionskostenFix).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
                })()}
              </Typography>
            </Box>
          </Box>

          <Divider />

          {/* Weitere Informationen */}
          <Typography variant="h6">Weitere Informationen</Typography>

          <FormControl fullWidth>
            <InputLabel>Versand Merker</InputLabel>
            <Select
              multiple
              value={form.versandmerker ? form.versandmerker.split(',') : []}
              onChange={(e) => {
                const selected = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                setForm({ ...form, versandmerker: selected.join(',') });
              }}
              label="Versand Merker"
              disabled={saving}
              renderValue={(selected) => (selected as string[]).join(',')}
            >
              <MenuItem value="LD">LD</MenuItem>
              <MenuItem value="BBK">BBK</MenuItem>
              <MenuItem value="GZG">GZG</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Bemerkungen"
            value={form.bemerkungen_aktion}
            onChange={(e) => setForm({ ...form, bemerkungen_aktion: e.target.value })}
            disabled={saving}
            multiline
            rows={3}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Abbrechen
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{ backgroundColor: '#003366' }}
          disabled={saving || !form.campaign_id || !form.lotterie_id}
        >
          {saving ? 'Speichern...' : 'Speichern'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
